import { Device, WindowSize } from '../../helpers'
import { doOnce } from '../../helpers/doOnce'
import { getQueryParam, queryParamHas } from '../../helpers/getQueryParam'
import { getIntersectionObserver } from '../../helpers/Observer'
import WebModel from '../../models/WebModel'
import { Slot } from '../Slot'
import SlotRepository from '../SlotRepository'
import { railFrameSelector } from './RailStructure'

export const interscrollerOuterClass = 'mv-interscroller-outer'
export const interscrollerInnerClass = 'mv-interscroller-inner'
export const interscrollerMinHeight = 401

const adReportSpace = 22
const maxAdHeight = 600
const minPercentForViewable = 0.51
const minHeightToBeViewable =
  maxAdHeight * minPercentForViewable + adReportSpace + 1

const interscrollerObserverConfig = {
  threshold: 0,
  rootMargin: '200px 0px 200px 0px'
}

// various query param controls to change behavior
const forceInterscroll = queryParamHas('forceInterscroll', 'true')
const forceTallOnly = queryParamHas('forceTallOnly', 'true')
let queryParamShift = getQueryParam('parallaxShift') || ''
if (queryParamShift && typeof queryParamShift === 'object') {
  queryParamShift = queryParamShift[0]
}
const queryParamShiftInt = parseInt(queryParamShift, 10) || 0
const queryParamFast = queryParamHas('parallaxFast', 'true')
const queryParamDisable = queryParamHas('parallaxDisable', 'true')

export interface IInterscrollerSlot {
  isInWindow: boolean
  interscrollerEnalbedForSlot: boolean
  interscrollerLayoutApplies: boolean
  applyInterscrollerLayout: () => void
  removeInterscrollerLayout: () => void
  updateParallaxEffect: () => void
}

export const hasInterscroller = (target: Constructor<Slot>): any =>
  class extends target implements IInterscrollerSlot {
    isInWindow: boolean = false

    constructor(...args) {
      super(...args)

      if (!interscrollerIsEnabled(this.model)) {
        return
      }

      this.setupObserver(this)
      if (this.adBoxConfig) {
        this.adBoxConfig.heightPxToContain = minHeightToBeViewable
      }
      this.rail.setupWrapElementListener()
    }

    validate() {
      return (
        super.validate() &&
        /** Ensure a slot gets a valid interscroller config if applied */
        hasValidInterscrollerConfig(this)
      )
    }

    get sizes(): AdSizes {
      const currentSizes = super.sizes
      if (!this.interscrollerEnalbedForSlot) {
        this.refreshSizeRestricted = true
        return currentSizes
      }
      const newSizes: AdSizes = [
        [320, 480],
        [300, 600]
      ]

      if (!forceTallOnly) {
        newSizes.unshift(...currentSizes)
      }

      return newSizes
    }

    set sizes(updatedAdSizes: AdSizes) {
      super.sizes = updatedAdSizes
    }

    applyInterscrollerLayout() {
      const adReportSpace = this.rail.spaceNeededForAdReport()

      this.observeRail()
      addInterscrollerClasses(this.rail.frame, this.wrapper)
      this.rail.frame.style.height = `calc(100% - ${adReportSpace}px)`

      window.addEventListener('scroll', this.updateParallaxEffect, {
        passive: true,
        capture: false
      })
      this.updateParallaxEffect()
    }

    removeInterscrollerLayout() {
      removeInterscrollerClasses(this.rail.frame, this.wrapper)
      window.removeEventListener('scroll', this.updateParallaxEffect)
      this.wrapper.style.transform = ''
      this.rail.frame.style.height = ''
    }

    updateParallaxEffect = () => {
      if (!this.isInWindow) {
        return
      }

      const unavailableTopOfScreen =
        (Device.isMobileOrTablet && this.model.mobile_header_offset) || 0
      const unavailableBottomOfScreen = document.querySelector('body.adhesion')
        ? 50
        : 0
      const parallaxShift = computerParallaxForSlot(
        this,
        unavailableTopOfScreen,
        unavailableBottomOfScreen
      )
      this.wrapper.style.transform = `translateY(${parallaxShift}px)`
    }

    get interscrollerEnalbedForSlot(): boolean {
      return (
        interscrollerIsEnabled(this.model) &&
        !!this.interscrollerConfig?.isSlotValid()
      )
    }

    get interscrollerLayoutApplies(): boolean {
      return (
        this.interscrollerEnalbedForSlot &&
        this.height >= interscrollerMinHeight
      )
    }

    private setupObserver = doOnce((slot: any) => {
      getIntersectionObserver().onChange((elements) => {
        elements.forEach(({ target, intersectionRatio }) => {
          const slotId = target.getAttribute('data-slotid') || ''
          const targetSlot = SlotRepository.getSlotById(slotId)

          if (!targetSlot || !(slot === targetSlot)) {
            return
          }

          const railSlide = slot.rail.slide
          if (railSlide && intersectionRatio > 0) {
            slot.isInWindow = true
            railSlide.style.display = ''
          } else if (railSlide) {
            slot.isInWindow = false
            railSlide.style.display = 'none'
          }
        })
      }, interscrollerObserverConfig)
    })

    private observeRail = () => {
      this.setupObserver(this)

      getIntersectionObserver().observe(
        this.rail.frame,
        interscrollerObserverConfig
      )
    }
  }

export function interscrollerIsEnabled(
  model: Pick<WebModel, 'interscroller_mobile' | 'interscroller_desktop'>
): boolean {
  if (forceInterscroll) {
    return true
  }

  return (
    (Device.isMobileOrTablet && model.interscroller_mobile) ||
    (Device.isDesktop && model.interscroller_desktop)
  )
}

function removeInterscrollerClasses(
  outerEl: HTMLElement,
  innerEl: HTMLElement
) {
  outerEl.classList.remove(interscrollerOuterClass)
  innerEl.classList.remove(interscrollerInnerClass)
}

function addInterscrollerClasses(outerEl: HTMLElement, innerEl: HTMLElement) {
  outerEl.classList.add(interscrollerOuterClass)
  innerEl.classList.add(interscrollerInnerClass)
}

function hasValidInterscrollerConfig(slot: Slot): boolean {
  return !!slot.interscrollerConfig
}

export interface IInterscrollerConfig {
  isSlotValid: () => boolean
}

function computerParallaxForSlot(
  slot: Slot,
  unavailableTopOfScreen: number,
  unavailableBottomOfScreen: number
) {
  const DIST_topBoundary = unavailableTopOfScreen
  const DIST_bottomBoundary = unavailableBottomOfScreen

  const DIST_viewportHeight = WindowSize.height
  const { top: COORD_railFrameTop, height: DIST_railFrameHeight } =
    slot.rail.frame.getBoundingClientRect()

  const DIST_functionalViewportHeight =
    DIST_viewportHeight - DIST_topBoundary - DIST_bottomBoundary
  const DIST_viewportTranslationSpace =
    DIST_functionalViewportHeight - slot.height

  const COORD_midTranslate =
    DIST_viewportTranslationSpace / 2 + DIST_topBoundary

  if (queryParamDisable || (Device.isIOS && Device.browser === 'Safari')) {
    return COORD_midTranslate
  }

  const COORD_startParallaxAt = DIST_functionalViewportHeight + DIST_topBoundary
  const COORD_endParallaxAt = -DIST_railFrameHeight + DIST_topBoundary

  let DIST_parallaxRange: number = DIST_viewportTranslationSpace / 2
  if (queryParamFast) {
    DIST_parallaxRange =
      DIST_functionalViewportHeight - DIST_viewportTranslationSpace / 2
  }
  if (queryParamShiftInt) {
    DIST_parallaxRange = queryParamShiftInt
  }

  if (
    COORD_railFrameTop <= COORD_startParallaxAt &&
    COORD_railFrameTop >= COORD_endParallaxAt
  ) {
    const pixelsThroughParallaxFrame =
      COORD_startParallaxAt - COORD_railFrameTop
    const pixelsInParallaxFrame = COORD_startParallaxAt - COORD_endParallaxAt
    const SCALAR_percentThroughParllaxFrame =
      pixelsThroughParallaxFrame / pixelsInParallaxFrame

    const COORD_resultingParallaxShift =
      (1 - 2 * SCALAR_percentThroughParllaxFrame) * DIST_parallaxRange +
      COORD_midTranslate

    return COORD_resultingParallaxShift
  }
}

export const interscrollerOuterStyles = `
	.${interscrollerOuterClass},
	.${interscrollerOuterClass}.${railFrameSelector} {
		position: absolute;
		display: block;
		margin: 0 auto;
		inset: 0px;
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
		clip: rect(0px, auto, auto, 0px);
	}
`

export const interscrollerInnerStyles = `
	.${interscrollerInnerClass},
	.mv-ad-box .adunitwrapper.${interscrollerInnerClass}{
		position: fixed;
		top: 0;
	}
`
